import React, { useEffect, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import { useBreakpointDown, useIsCoarseTablet } from '@shared/hooks/useBreakpoint';

import { useStyles } from './NeoAnalyticsPage.styles';
import { NeoAnalyticsSideBar } from '../NeoAnalyticsSideBar/NeoAnalyticsSideBar';

import { useExecutionsGridUIStore, useExecutionsUIStore } from '@core/useStores';

import { ThemeProps } from '@styles/theme';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { UserExecutions } from '../UserExecutions';
import { ProcedureExecutions } from '../ProcedureExecutions';
import { ExecutionAnalytics } from '../ExecutionAnalytics';
import { Button } from '@shared/components/Button';
import BackIcon from '@assets/left-icon.svg';
import { RoutePaths } from '@core/routes/RoutePaths';
import { messages } from 'NeoAnalytics/neoAnalytics.messages';

type NeoAnalyticsProps = {
    fromGroup?: boolean;
}


export const NeoAnalyticsPage: React.FC<NeoAnalyticsProps> = ({fromGroup = false}) =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    // The side panel competes with the grading/analytics content, so touch
    // tablets up to ~11" in landscape also get the drawer; only bigger slates
    // keep the inline sidebar.
    const isBelowTablet = useBreakpointDown('tablet');
    const isCoarseTablet = useIsCoarseTablet();
    const isTabletLayout = isBelowTablet || isCoarseTablet;
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const params = useParams();
    const { id, executionId } = params;

    const executionsGridUIStore = useExecutionsGridUIStore();
    const executionsUIStore = useExecutionsUIStore();

    const canGoBackInContent = true;

    const goBackInContent = () => {
        if (executionId && id) {
            if ((location.state as any)?.fromUserInsights) {
                navigate(`/${RoutePaths.users}/analytics/${id}/insights`);

                return;
            }
            const baseRoute = fromGroup ? RoutePaths.groupNeoAnalytics : RoutePaths.usersNeoAnalytics;
            navigate(`/${baseRoute.replace(':id', id)}`);

            return;
        }

        if (executionsGridUIStore.analyticsEntityId) {
            executionsGridUIStore.setParams({
                page: 0,
                entityIds: [],
                fullData: false,
            });
            executionsGridUIStore.clearAnalyticsEntityId();

            return;
        }

        executionsGridUIStore.setParams({
            page: 0,
            actorIds: [],
            entityIds: [],
            fullData: false,
        });
        executionsGridUIStore.clearData();
        executionsUIStore.clearData();

        if (fromGroup) {
            navigate(`/${RoutePaths.groups}`);
        } else {
            navigate(`/${RoutePaths.users}`);
        }
    };

    const goToInsights = () => {
        if (id && !fromGroup) {
            navigate(`/${RoutePaths.users}/analytics/${id}/insights`);
        }
    };

    useEffect(() =>{
        executionsGridUIStore.clearData();
        executionsUIStore.clearData();

        executionsGridUIStore.setAnalyticsActorId(id!);
        if (!fromGroup) {
            executionsUIStore.setExecutionsUserId(id!);
        }

        executionsGridUIStore.setParams({
            page: 0,
            limit: 100,
            entityIds: [],
            actorIds: [id!],
        });

        executionsGridUIStore.togglePageActivity(true);

        return () => {
            executionsGridUIStore.togglePageActivity(false);
            executionsGridUIStore.clearData();
            executionsUIStore.clearData();
        };
    }, [fromGroup, id])

    useEffect(() => {
        if (executionId) {
            void executionsUIStore.loadExecution(executionId);

            return;
        }

        executionsUIStore.clearSelectedExecutionId();
    }, [executionId, executionsUIStore]);

    useEffect(() => {
        if (!isTabletLayout) {
            setSidebarOpen(false);
        }
    }, [isTabletLayout]);

    return (
        <div className={styles.mainWrapper}>
            {!isTabletLayout && <NeoAnalyticsSideBar fromGroup={fromGroup} onInsights={!fromGroup ? goToInsights : undefined}/>}
            <div className={styles.body}>
                {isTabletLayout && (
                    <div className={styles.tabletSidebarToggleRow}>
                        {canGoBackInContent && (
                            <Button className={styles.tabletBackButton} type="button" color="secondary" onClick={goBackInContent}>
                                <img src={BackIcon} alt={messages['neoAnalytics.toggle.back']} className={styles.tabletBackButtonIcon} />
                                {messages['neoAnalytics.toggle.back']}
                            </Button>
                        )}
                        <Button className={styles.tabletSidebarToggle} type="button" onClick={() => setSidebarOpen(true)}>
                            {executionId ? messages['neoAnalytics.toggle.sessionInfo'] : messages['neoAnalytics.toggle.sessions']}
                        </Button>
                        {!fromGroup && (
                            <Button className={styles.tabletSidebarToggle} type="button" onClick={goToInsights}>
                                {messages['neoAnalytics.toggle.insights']}
                            </Button>
                        )}
                    </div>
                )}
                <Observer>
                    {() => {
                        if(executionsUIStore.selectedExecutionId){
                            return (<ExecutionAnalytics key={`execution-${id}-${executionsUIStore.selectedExecutionId}`} />);
                        }else if(!executionsGridUIStore.analyticsEntityId){
                            return (<UserExecutions key={`user-executions-${id}`} />);
                        }else {
                            return (<ProcedureExecutions key={`procedure-${id}-${executionsGridUIStore.analyticsEntityId}`} />);
                        }
                    }}
                </Observer>
            </div>
            {isTabletLayout && (
                <Drawer
                    anchor="left"
                    open={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                >
                    <NeoAnalyticsSideBar fromGroup={fromGroup} onInsights={!fromGroup ? goToInsights : undefined}/>
                </Drawer>
            )}
        </div>
    )
}